import { useCallback, useEffect, useRef, useState } from "react";

// ---------------------------------------------------------------
// useCardTilt — mouse-tracking parallax tilt + numeric-change
// ripple effects for the Overview widgets. Returns a `bind` object
// meant to be spread on the card's root wrapper, and a `Ripples`
// element that renders the animated ring pulses inside the card.
// Also fires when `pulseKey` changes (e.g. numeric total updates).
// Respects prefers-reduced-motion.
// ---------------------------------------------------------------
export function useCardTilt({ pulseKey, maxTilt = 6, perspective = 1000 } = {}) {
  const ref = useRef(null);
  const [ripples, setRipples] = useState([]);
  const reduced = typeof window !== "undefined"
    && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

  const onMouseMove = useCallback((e) => {
    if (reduced) return;
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width;   // 0..1
    const py = (e.clientY - r.top) / r.height;   // 0..1
    const rx = ((py - 0.5) * -2 * maxTilt).toFixed(2);
    const ry = ((px - 0.5) *  2 * maxTilt).toFixed(2);
    el.style.transform = `perspective(${perspective}px) rotateX(${rx}deg) rotateY(${ry}deg)`;
    // Cursor-following sheen — write CSS vars consumed by the `.cosmic-sheen`
    // ::before overlay (radial-gradient positioned at these coords). Alpha
    // ramps up while the cursor is inside the card.
    el.style.setProperty("--sheen-x", `${(px * 100).toFixed(2)}%`);
    el.style.setProperty("--sheen-y", `${(py * 100).toFixed(2)}%`);
    el.style.setProperty("--sheen-a", "1");
  }, [maxTilt, perspective, reduced]);

  const onMouseLeave = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    el.style.transform = `perspective(${perspective}px) rotateX(0deg) rotateY(0deg)`;
    // Fade sheen back out; leave last x/y so the fade emanates from where the
    // cursor was, not from the center.
    el.style.setProperty("--sheen-a", "0");
  }, [perspective]);

  const fireRipple = useCallback(() => {
    if (reduced) return;
    const id = Date.now() + Math.random();
    setRipples((prev) => [...prev, id]);
    setTimeout(() => {
      setRipples((prev) => prev.filter((x) => x !== id));
    }, 1600);
  }, [reduced]);

  useEffect(() => {
    if (pulseKey === undefined || pulseKey === null) return;
    fireRipple();
  }, [pulseKey, fireRipple]);

  const Ripples = () => (
    <div className="pointer-events-none absolute inset-0 overflow-hidden rounded-2xl" aria-hidden="true">
      {ripples.map((id) => (
        <span
          key={id}
          className="absolute left-1/2 top-1/2 cosmic-ripple-ring"
          data-testid="cosmic-ripple-ring"
        />
      ))}
    </div>
  );

  const bind = {
    ref,
    onMouseMove,
    onMouseLeave,
    style: { transformStyle: "preserve-3d", transition: "transform 320ms cubic-bezier(0.22, 1, 0.36, 1)", willChange: "transform" },
  };

  return { bind, Ripples, fireRipple };
}
